/**
 * Pure maths for the pointer/gesture layer (tested without a DOM).
 */
import { TAU } from './geometry';

export const DOUBLE_TAP_MS = 300;
export const LONG_PRESS_MS = 600;
export const VELOCITY_FULL_PX_PER_S = 1500;
export const LONG_PRESS_VELOCITY_FLOOR = 0.4;
export const SWIPE_MIN_PX = 60;

/** Map px/s to 0..1: 0 at rest, 1 at ~1500 px/s (soft knee so slow movement still registers). */
export function normaliseVelocity(pxPerSec: number, full = VELOCITY_FULL_PX_PER_S): number {
  const v = Math.max(0, pxPerSec) / full;
  return Math.min(1, 1 - Math.exp(-2.2 * v));
}

/** Exponentially smoothed pointer speed. Fast to rise, slow to settle, so a flick reads as energy. */
export class VelocitySmoother {
  private lastX = 0; private lastY = 0; private lastT = 0; private started = false;
  /** smoothed px/s */
  value = 0;
  constructor(private readonly attack = 0.5, private readonly release = 0.12) {}
  reset(x: number, y: number, t: number): void { this.lastX = x; this.lastY = y; this.lastT = t; this.started = true; this.value = 0; }
  /** Feed a sample; returns the smoothed px/s. `t` in ms. */
  push(x: number, y: number, t: number): number {
    if (!this.started) { this.reset(x, y, t); return 0; }
    const dt = Math.max(1, t - this.lastT) / 1000;
    const inst = Math.hypot(x - this.lastX, y - this.lastY) / dt;
    const k = inst > this.value ? this.attack : this.release;
    this.value += (inst - this.value) * k;
    this.lastX = x; this.lastY = y; this.lastT = t;
    return this.value;
  }
  /** Decay towards rest when no sample arrives (call per frame). */
  idle(t: number): number {
    if (!this.started) return 0;
    const dt = Math.max(0, t - this.lastT) / 1000;
    if (dt > 0.05) this.value *= Math.exp(-dt * 6);
    return this.value;
  }
}

export interface TapRecord { zoneKey: string; time: number; x: number; y: number }

/** A second tap within 300 ms on the same zone (and near the first) is a double-tap. */
export function isDoubleTap(prev: TapRecord | null, next: TapRecord, ms = DOUBLE_TAP_MS, maxDist = 40): boolean {
  if (!prev) return false;
  if (next.time - prev.time > ms || next.time < prev.time) return false;
  if (prev.zoneKey !== next.zoneKey) return false;
  return Math.hypot(next.x - prev.x, next.y - prev.y) <= maxDist;
}

/** Signed shortest angular difference b - a in (-π, π]. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

/**
 * The prayer wheel: circular motion in the fire ring spins a global value. Angular velocity (rad/s) is accumulated
 * while spinning and decays like a wheel when let go.
 */
export class PrayerWheel {
  /** current angular velocity, rad/s (counter-clockwise positive) */
  omega = 0;
  /** accumulated angle, rad */
  angle = 0;
  private lastAngle: number | null = null;
  private lastT = 0;
  constructor(private readonly friction = 0.8) {}
  spin(angle: number, t: number): void {
    if (this.lastAngle !== null) {
      const dt = Math.max(1, t - this.lastT) / 1000;
      const w = angleDelta(this.lastAngle, angle) / dt;
      this.omega += (w - this.omega) * 0.4;
    }
    this.lastAngle = angle; this.lastT = t;
  }
  release(): void { this.lastAngle = null; }
  /** advance by dt seconds; returns omega */
  step(dt: number): number {
    if (this.lastAngle === null) this.omega *= Math.exp(-dt * this.friction);
    if (Math.abs(this.omega) < 1e-3) this.omega = 0;
    this.angle += this.omega * dt;
    return this.omega;
  }
}

/** Two-finger horizontal swipe: both pointers moved the same way by ≥ 60 px with little vertical travel. */
export function detectTwoFingerSwipe(
  a: { x0: number; y0: number; x: number; y: number }, b: { x0: number; y0: number; x: number; y: number }, minPx = SWIPE_MIN_PX,
): -1 | 0 | 1 {
  const dxa = a.x - a.x0, dxb = b.x - b.x0;
  const dya = a.y - a.y0, dyb = b.y - b.y0;
  if (Math.abs(dxa) < minPx || Math.abs(dxb) < minPx) return 0;
  if (Math.sign(dxa) !== Math.sign(dxb)) return 0;
  if (Math.abs(dya) > Math.abs(dxa) * 0.6 || Math.abs(dyb) > Math.abs(dxb) * 0.6) return 0;
  return dxa > 0 ? 1 : -1;
}

/** Wheel delta → macro nudge in −0.1..0.1 per event (pixels vs lines normalised). */
export function wheelToNudge(deltaY: number, deltaMode: number): number {
  const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  return -Math.max(-0.1, Math.min(0.1, px / 600));
}
